import { useState } from 'react';
import { Link } from 'react-router-dom';
import { nomeCompleto, type BottleDTO } from '@adega/shared';
import { EmptyState, ErrorBox, Loading } from '../components/Feedback.js';
import { TacaVazia } from '../components/TacaVazia.js';
import { PageHeader } from '../components/PageHeader.js';
import { useBottles, useDestaquesDoConsumo } from '../api/queries.js';
import { porExtenso } from '../lib/datas.js';

/**
 * "Já bebi": o histórico, das mais recentes para as mais antigas.
 *
 * Antes desta tela, marcar uma garrafa como bebida a fazia desaparecer: sobrava
 * só o contador "x já bebidas". É também o lugar onde a nota e o comentário
 * viram algo consultável — e é deles que sai a "uva preferida" dos números.
 */
/** As duas perguntas que o histórico responde. */
type Ordem = 'recentes' | 'notas';

/**
 * Ordena sem mexer na lista original.
 *
 * Por nota, garrafa sem nota vai para o fim: ela não é pior que uma de 1 estrela,
 * é desconhecida — e no meio do ranking só atrapalharia a leitura. Empate de nota
 * desempata pela data, a mais recente primeiro.
 */
function ordenar(bebidas: BottleDTO[], ordem: Ordem): BottleDTO[] {
  const quando = (garrafa: BottleDTO) => new Date(garrafa.consumedAt ?? 0).getTime();

  return [...bebidas].sort((a, b) => {
    if (ordem === 'notas' && a.rating !== b.rating) {
      return (b.rating ?? -1) - (a.rating ?? -1);
    }
    return quando(b) - quando(a);
  });
}

/**
 * Destaques do que já foi bebido.
 *
 * **Cada linha só existe quando o dado a sustenta** — quem bebeu uma garrafa vê
 * uma linha, quem bebeu nenhuma não vê o bloco. Em produção uma pessoa tem
 * dezoito bebidas e todas as outras têm uma ou nenhuma, então o estado quase
 * vazio é o comum, não a exceção.
 *
 * Compacto de propósito: no celular esta tela já gasta altura com cabeçalho,
 * contagem e alternador antes do primeiro card, e **a lista é o produto**. Três
 * linhas de texto, sem números gigantes e sem painel.
 */
function Destaques() {
  const { data } = useDestaquesDoConsumo();
  if (!data) return null;

  const { noMes, repetido, melhor, safraMaisAntiga, paises } = data;
  if (!noMes && !repetido && !melhor && !safraMaisAntiga && !paises) return null;

  const mes = new Date().toLocaleDateString('pt-BR', { month: 'long' });

  /*
   * Números viram ladrilho; só o vinho repetido continua frase.
   *
   * Cinco períodos empilhados liam como parágrafo: o olho tinha de LER tudo
   * para entender, em vez de bater e entender. Com o número à frente, cada
   * destaque se lê de relance.
   *
   * O repetido não cabe num ladrilho porque o nome do vinho é o conteúdo —
   * "2" sozinho não diz nada ali.
   */
  const ladrilhos: Array<{ valor: string; rotulo: string }> = [
    noMes
      ? {
          valor: String(noMes.quantidade),
          rotulo: `${noMes.quantidade === 1 ? 'garrafa aberta' : 'garrafas abertas'} em ${mes}`,
        }
      : null,
    safraMaisAntiga
      ? {
          valor: `${safraMaisAntiga.idadeAoAbrir} anos`,
          rotulo: `a mais velha que abriu — safra ${safraMaisAntiga.vintage}`,
        }
      : null,
    melhor
      ? {
          valor: `${melhor.rating} ★`,
          rotulo:
            melhor.empatadas === 1
              ? 'sua nota máxima, numa garrafa'
              : `sua nota máxima, em ${melhor.empatadas} garrafas`,
        }
      : null,
    paises ? { valor: String(paises.quantidade), rotulo: 'países de origem' } : null,
  ].filter((item): item is { valor: string; rotulo: string } => item !== null);

  return (
    <section className="space-y-2">
      {/* Duas colunas no celular, quatro a partir de telas largas: esticar um
          ladrilho até 580px deixa o número perdido num mar de vazio. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {ladrilhos.map((item, i) => (
          <div
            key={item.rotulo}
            /* Contagem ímpar deixaria um ladrilho sozinho e meio: o último
               ocupa a linha inteira em vez de ficar torto. */
            className={`card px-3 py-3 ${
              ladrilhos.length % 2 === 1 && i === ladrilhos.length - 1
                ? 'col-span-2 sm:col-span-1'
                : ''
            }`}
          >
            {/* Sem `tabular-nums`: ele dá a todo dígito a largura do zero, e
                número grande fica frouxo. Tabular é para coluna que alinha. */}
            <p className="text-2xl font-semibold leading-none text-adega-gold">{item.valor}</p>
            <p className="mt-1.5 text-xs leading-snug text-adega-muted">{item.rotulo}</p>
          </div>
        ))}
      </div>

      {repetido ? (
        <p className="card px-3 py-3 text-sm leading-relaxed">
          Você voltou <strong className="font-semibold">{repetido.vezes} vezes</strong> ao mesmo
          vinho: {repetido.nome}.
        </p>
      ) : null}
    </section>
  );
}

export function DrunkBottles() {
  const { data: bebidas, isLoading, error } = useBottles({ status: 'consumed' });
  const [ordem, setOrdem] = useState<Ordem>('recentes');

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Já bebi"
        // O subtítulo acompanha a ordem escolhida: dizer "da mais recente" com a
        // lista ordenada por nota faria a tela contradizer a si mesma.
        subtitulo={
          ordem === 'recentes'
            ? 'Da mais recente para a mais antiga'
            : 'Das melhores notas para as menores'
        }
        voltar="/"
      />

      {error ? <ErrorBox erro={error} /> : null}

      {bebidas && bebidas.length === 0 ? (
        <EmptyState
          titulo="Nenhuma garrafa bebida ainda"
          descricao='Quando você marcar "Bebi esta", a garrafa vem para cá com a data, a nota e o que você achou.'
          acao={
            <Link to="/" className="btn-primary">
              Ver minhas adegas
            </Link>
          }
        />
      ) : null}

      {bebidas && bebidas.length > 0 ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-adega-muted">
              {bebidas.length === 1 ? '1 garrafa' : `${bebidas.length} garrafas`}
              {' · '}
              {bebidas.filter((garrafa) => garrafa.rating !== null).length} com nota
            </p>

            {/* Duas ordens porque são duas perguntas: "o que andei bebendo" e
                "o que vale repetir na próxima compra". */}
            <div className="flex shrink-0 rounded-full bg-adega-surface p-0.5 text-xs">
              {(
                [
                  ['recentes', 'Recentes'],
                  ['notas', 'Melhores'],
                ] as Array<[Ordem, string]>
              ).map(([valor, rotulo]) => (
                <button
                  key={valor}
                  type="button"
                  aria-pressed={ordem === valor}
                  onClick={() => setOrdem(valor)}
                  className={`rounded-full px-3 py-1.5 transition ${
                    ordem === valor ? 'bg-adega-wine font-medium text-white' : 'text-adega-muted'
                  }`}
                >
                  {rotulo}
                </button>
              ))}
            </div>
          </div>

          <Destaques />

          <ul className="space-y-2">
            {ordenar(bebidas, ordem).map((garrafa) => (
              <li key={garrafa.id}>
                <Link
                  to={`/garrafa/${garrafa.id}`}
                  className="card flex items-start gap-3 transition active:scale-[0.99]"
                >
                  {garrafa.thumbUrl ? (
                    <img
                      src={garrafa.thumbUrl}
                      alt=""
                      className="h-16 w-12 shrink-0 rounded-lg object-cover opacity-80"
                    />
                  ) : (
                    <span
                      aria-hidden
                      className="grid h-16 w-12 shrink-0 place-items-center rounded-lg bg-adega-surface-2 text-adega-muted"
                    >
                      <TacaVazia className="h-6 w-6" />
                    </span>
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{nomeCompleto(garrafa)}</p>
                    <p className="text-xs text-adega-muted">
                      {porExtenso(garrafa.consumedAt)}
                      {garrafa.vintage ? ` · safra ${garrafa.vintage}` : ''}
                    </p>
                    {garrafa.consumptionNote ? (
                      <p className="mt-1 line-clamp-2 text-sm text-adega-muted">
                        {garrafa.consumptionNote}
                      </p>
                    ) : null}
                  </div>

                  {garrafa.rating !== null ? (
                    <span className="shrink-0 text-sm text-adega-gold" aria-label={`${garrafa.rating} de 5`}>
                      {'★'.repeat(garrafa.rating)}
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
